import { Helmet } from "react-helmet-async"
import { useLocation } from "react-router-dom"

const SITE_URL = "https://tbgestionimmobiliere.com"
const SITE_NAME = "TB Gestion Immobilière"
const DEFAULT_IMAGE = `${SITE_URL}/img/bg.jpg`

type PageDefinition = {
  title: string
  description: string
  label: string
  robots?: string
  image?: string
}

const indexRobots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
const noIndexRobots = "noindex, follow"

const portalPage: PageDefinition = {
  title: "Espace de gestion | TB Gestion",
  description: "Espace de gestion sécurisé de TB Gestion Immobilière.",
  label: "Espace de gestion",
  robots: noIndexRobots,
}

const notFoundPage: PageDefinition = {
  title: `Page introuvable | ${SITE_NAME}`,
  description: "La page demandée n’est pas disponible.",
  label: "Page introuvable",
  robots: noIndexRobots,
}

const pages: Record<string, PageDefinition> = {
  "/": {
    title: "Gestion immobilière à Portneuf et Québec | TB Gestion",
    description:
      "Gestion locative, sélection des locataires, entretien et suivi administratif pour les propriétaires de Portneuf et de l’ouest de Québec.",
    label: "Accueil",
  },
  "/services-gestion-immobiliere": {
    title: "Services de gestion immobilière | TB Gestion",
    description:
      "Découvrez nos services de gestion locative : perception des loyers, relations locataires, entretien, mise en location et suivi administratif.",
    label: "Services de gestion immobilière",
    image: `${SITE_URL}/img/modern-apartment.jpg`,
  },
  "/gestion-locative-portneuf": {
    title: "Gestion locative à Portneuf et Québec | TB Gestion",
    description: "Gestion locative d’immeubles résidentiels à Neuville, Donnacona, Cap-Santé, Pont-Rouge, Portneuf et dans l’ouest de Québec.",
    label: "Gestion locative à Portneuf",
    image: `${SITE_URL}/img/about-hero.jpg`,
  },
  "/gestion-chalets-condos-portneuf": {
    title: "Gestion de chalets et condos à Portneuf | TB Gestion",
    description:
      "Gestion de locations saisonnières à Portneuf et dans l’ouest de Québec : réservations, communication voyageurs, entretien et suivi opérationnel.",
    label: "Gestion de chalets et condos",
    image: `${SITE_URL}/img/cta-management.jpg`,
  },
  "/guide-proprietaires-portneuf": {
    title: "Guide pour propriétaires à Portneuf | TB Gestion",
    description:
      "Ressources pratiques pour les propriétaires d’immeubles à Portneuf : entretien saisonnier, gestion locative et préparation des logements.",
    label: "Guide pour propriétaires",
    image: `${SITE_URL}/img/interieur-maison-unite.jpg`,
  },
  "/blog/reglementation-immeubles-portneuf": {
    title: "Réglementation des immeubles à revenus | TB Gestion",
    description:
      "Points de vigilance pour les propriétaires d’immeubles à revenus : règles municipales, sécurité, entretien et sources officielles à consulter.",
    label: "Réglementation des immeubles",
  },
  "/blog/preparer-plex-hiver-neuville": {
    title: "Préparer un plex pour l’hiver à Neuville | TB Gestion",
    description:
      "Une liste de vérification concrète pour préparer un plex à l’hiver : chauffage, toiture, plomberie, accès, sécurité et communications.",
    label: "Préparer un plex pour l’hiver",
  },
  "/blog/optimiser-chalets-locatifs": {
    title: "Mieux gérer un chalet locatif à Portneuf | TB Gestion",
    description:
      "Conseils pratiques pour structurer la gestion d’un chalet locatif : calendrier, entretien, accueil, tarification et suivi des dépenses.",
    label: "Mieux gérer un chalet locatif",
  },
  "/a-propos": {
    title: "À propos de TB Gestion Immobilière",
    description:
      "Découvrez l’approche locale, transparente et proactive de TB Gestion Immobilière pour les propriétaires de Portneuf et de l’ouest de Québec.",
    label: "À propos",
    image: `${SITE_URL}/img/about-hero.jpg`,
  },
  "/nous-contacter": {
    title: "Contacter TB Gestion Immobilière",
    description: "Parlez-nous de votre immeuble ou posez-nous une question. TB Gestion Immobilière dessert Portneuf et la couronne ouest de Québec.",
    label: "Nous contacter",
  },
  "/politique-de-confidentialite": {
    title: "Politique de confidentialité | TB Gestion",
    description: "Consultez la politique de confidentialité et les pratiques de protection des renseignements personnels de TB Gestion Immobilière.",
    label: "Politique de confidentialité",
  },
  "/conditions-utilisation": {
    title: "Conditions d’utilisation | TB Gestion",
    description: "Consultez les conditions d’utilisation du site de TB Gestion Immobilière.",
    label: "Conditions d’utilisation",
    robots: noIndexRobots,
  },
  "/faire-une-demande": {
    title: "Demande de consultation | TB Gestion",
    description: "Présentez votre parc immobilier à TB Gestion Immobilière afin de planifier un premier échange.",
    label: "Demande de consultation",
    robots: noIndexRobots,
  },
  "/demande-de-location": {
    title: "Demande de location | TB Gestion",
    description: "Formulaire de demande de location pour les logements gérés par TB Gestion Immobilière.",
    label: "Demande de location",
    robots: noIndexRobots,
  },
  "/se-connecter": {
    title: "Connexion à Espace TB | TB Gestion",
    description: "Accès sécurisé des locataires, propriétaires et membres de l’équipe à Espace TB.",
    label: "Connexion à Espace TB",
    robots: noIndexRobots,
  },
  "/mot-de-passe-oublie": {
    title: "Récupérer votre accès Espace TB",
    description: "Demandez un lien sécurisé pour réinitialiser votre mot de passe Espace TB.",
    label: "Récupérer votre accès",
    robots: noIndexRobots,
  },
  "/definir-mot-de-passe": {
    title: "Définir votre mot de passe Espace TB",
    description: "Activez ou récupérez votre accès sécurisé Espace TB.",
    label: "Définir votre mot de passe",
    robots: noIndexRobots,
  },
}

const serviceTypes: Record<string, string> = {
  "/services-gestion-immobiliere": "Services de gestion immobilière",
  "/gestion-locative-portneuf": "Gestion locative d’immeubles résidentiels",
  "/gestion-chalets-condos-portneuf": "Gestion opérationnelle de chalets et condos locatifs",
}

const faqByPath: Record<string, Array<{ question: string; answer: string }>> = {
  "/gestion-locative-portneuf": [
    {
      question: "Quelle durée de mandat proposez-vous?",
      answer:
        "Nous recommandons un mandat d’au moins 12 mois pour assurer une transition stable et planifier les échéances. Les conditions de renouvellement sont précisées au contrat.",
    },
    {
      question: "Quel est votre processus de sélection des locataires?",
      answer:
        "Avec l’autorisation du candidat, nous réalisons les vérifications prévues au processus. Les mêmes critères sont appliqués de façon cohérente à chaque candidature.",
    },
    {
      question: "Gérez-vous aussi les travaux majeurs?",
      answer:
        "Nous pouvons coordonner les demandes d’estimation, présenter les budgets pour approbation et suivre les travaux selon les paramètres du mandat.",
    },
  ],
  "/gestion-chalets-condos-portneuf": [
    {
      question: "Comment gérez-vous le ménage et l’entretien?",
      answer: "Nous travaillons avec des équipes locales selon les standards convenus et une liste de vérification après chaque séjour.",
    },
    {
      question: "Pouvez-vous m’aider à obtenir ou renouveler l’attestation CITQ?",
      answer:
        "Nous pouvons vous accompagner dans la préparation du dossier et le suivi des échéances. Le propriétaire demeure responsable des renseignements fournis et des exigences applicables.",
    },
    {
      question: "Accompagnez-vous la mise en marché internationale?",
      answer: "Selon le mandat, nous pouvons adapter ou traduire les annonces et les communications destinées à une clientèle hors Québec.",
    },
  ],
}

const normalizePath = (pathname: string) => {
  if (pathname === "/") return pathname
  return pathname.replace(/\/+$/, "")
}

const SiteSeo = () => {
  const { pathname } = useLocation()
  const path = normalizePath(pathname)
  const page = pages[path] ?? (path.startsWith("/app") ? portalPage : notFoundPage)
  const canonical = `${SITE_URL}${path === "/" ? "/" : path}`
  const image = page.image ?? DEFAULT_IMAGE
  const robots = page.robots ?? (path.startsWith("/app") ? noIndexRobots : indexRobots)

  const organization = {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/android-chrome-512x512.png`,
      width: 512,
      height: 512,
    },
    image,
    email: "info@tbgestionimmobiliere.com",
    telephone: "+1-418-684-9004",
    description: "Services de gestion immobilière à Portneuf et dans l’ouest de Québec.",
    areaServed: ["Portneuf", "Neuville", "Donnacona", "Cap-Santé", "Pont-Rouge", "Saint-Augustin-de-Desmaures", "Québec"],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-418-684-9004",
      email: "info@tbgestionimmobiliere.com",
      contactType: "service à la clientèle",
      availableLanguage: ["fr"],
      areaServed: "CA-QC",
    },
  }

  const localBusiness = {
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    image,
    logo: `${SITE_URL}/android-chrome-512x512.png`,
    email: "info@tbgestionimmobiliere.com",
    telephone: "+1-418-684-9004",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Neuville",
      addressRegion: "QC",
      addressCountry: "CA",
    },
    areaServed: organization.areaServed,
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
  }

  const website = {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: SITE_NAME,
    alternateName: "TB Gestion",
    inLanguage: "fr-CA",
    publisher: { "@id": `${SITE_URL}/#organization` },
  }

  const breadcrumb =
    path === "/"
      ? undefined
      : {
          "@type": "BreadcrumbList",
          "@id": `${canonical}#breadcrumb`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: page.label, item: canonical },
          ],
        }

  const webPage = {
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    url: canonical,
    name: page.title,
    description: page.description,
    inLanguage: "fr-CA",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    ...(breadcrumb ? { breadcrumb: { "@id": `${canonical}#breadcrumb` } } : {}),
  }

  const service = serviceTypes[path]
    ? {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: serviceTypes[path],
        description: page.description,
        url: canonical,
        provider: { "@id": `${SITE_URL}/#localbusiness` },
        areaServed: organization.areaServed,
      }
    : undefined

  const faq = faqByPath[path]
    ? {
        "@type": "FAQPage",
        "@id": `${canonical}#faq`,
        mainEntity: faqByPath[path].map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      }
    : undefined

  const article = path.startsWith("/blog/")
    ? {
        "@type": "Article",
        "@id": `${canonical}#article`,
        headline: page.title.replace(` | ${SITE_NAME}`, ""),
        description: page.description,
        image,
        inLanguage: "fr-CA",
        author: { "@id": `${SITE_URL}/#organization` },
        publisher: { "@id": `${SITE_URL}/#organization` },
        dateModified: "2026-08-21",
        mainEntityOfPage: { "@id": `${canonical}#webpage` },
      }
    : undefined

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      organization,
      localBusiness,
      website,
      webPage,
      ...(breadcrumb ? [breadcrumb] : []),
      ...(service ? [service] : []),
      ...(faq ? [faq] : []),
      ...(article ? [article] : []),
    ],
  }

  return (
    <Helmet>
      <html lang="fr-CA" />
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <meta property="og:locale" content="fr_CA" />
      <meta property="og:type" content={path.startsWith("/blog/") ? "article" : "website"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content="TB Gestion Immobilière" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={page.title} />
      <meta name="twitter:description" content={page.description} />
      <meta name="twitter:image" content={image} />
      {!robots.startsWith("noindex") && <script type="application/ld+json">{JSON.stringify(structuredData)}</script>}
    </Helmet>
  )
}

export default SiteSeo
